/**
 * Page admin : import xlsx Rentio en mode MERGE.
 *
 * Flow :
 *   1. Admin upload un .xlsx
 *   2. Parse côté browser via exceljs (dynamic import → lazy chunk)
 *   3. Applique le mapper Rentio (rowsToBiens) → { biens, locataires }
 *   4. Appelle Edge Function `import-xlsx-merge` en dryRun=true → preview stats
 *   5. Admin confirme → re-appelle l'Edge Function en mode réel
 *
 * Gate : profile.role === "admin" + JWT Supabase (pas de dev session).
 * Voir supabase/functions/import-xlsx-merge/index.ts pour la logique merge.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { SectionCard } from "@/components/layout/SectionCard";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  rowsToBiens,
  type BienInsertRow,
  type LocataireInsertRow,
  type MapperResult,
} from "@/lib/import/rentioMapper";

type ImportStats = {
  biensCreated: number;
  biensUpdated: number;
  biensSkipped: number;
  locatairesCreated: number;
  locatairesUpdated: number;
  errors: Array<{ id: string; table: string; message: string }>;
};

type ImportResponse = {
  ok?: boolean;
  dryRun?: boolean;
  stats?: ImportStats;
  error?: string;
};

async function parseXlsxInBrowser(file: File): Promise<Record<string, unknown>[]> {
  // Lazy chunk : exceljs (~1MB) ne charge que sur cette page admin
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const buf = await file.arrayBuffer();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  if (!ws) return [];

  const headers: string[] = [];
  ws.getRow(1).eachCell({ includeEmpty: true }, (c, col) => {
    headers[col - 1] = String(c.value ?? "");
  });

  const rows: Record<string, unknown>[] = [];
  ws.eachRow({ includeEmpty: false }, (row, n) => {
    if (n === 1) return;
    const r: Record<string, unknown> = {};
    row.eachCell({ includeEmpty: true }, (c, col) => {
      const h = headers[col - 1];
      if (!h) return;
      let val: unknown = c.value;
      if (val && typeof val === "object" && "text" in val) val = (val as { text: unknown }).text;
      if (val && typeof val === "object" && "result" in val) val = (val as { result: unknown }).result;
      r[h] = val;
    });
    rows.push(r);
  });
  return rows;
}

export function ImportXlsxPage() {
  const { t } = useTranslation();
  const { profile, isDevSession } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [mapperResult, setMapperResult] = useState<MapperResult | null>(null);
  const [dryRunStats, setDryRunStats] = useState<ImportStats | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Gate : admin seulement, et pas en dev session (Edge Function exige un vrai JWT)
  if (!profile || profile.role !== "admin") return <Navigate to="/v2/admin/dashboard" replace />;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setMapperResult(null);
    setDryRunStats(null);
    setImportResult(null);
    setError(null);
  };

  const callEdgeFunction = async (
    biens: BienInsertRow[],
    locataires: LocataireInsertRow[],
    dryRun: boolean,
  ): Promise<ImportResponse> => {
    const { data, error } = await supabase.functions.invoke<ImportResponse>("import-xlsx-merge", {
      body: { biens, locataires, dryRun },
    });
    if (error) return { error: error.message };
    return data ?? { error: "Réponse vide" };
  };

  const handleAnalyse = async () => {
    if (!file) return;
    setParsing(true);
    setError(null);
    setMapperResult(null);
    setDryRunStats(null);
    setImportResult(null);
    try {
      const rows = await parseXlsxInBrowser(file);
      const result = rowsToBiens(rows);
      setMapperResult(result);

      const resp = await callEdgeFunction(result.biens, result.locataires, true);
      if (resp.error) {
        setError(resp.error);
      } else {
        setDryRunStats(resp.stats ?? null);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erreur de parsing");
    } finally {
      setParsing(false);
    }
  };

  const handleConfirm = async () => {
    if (!mapperResult) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const resp = await callEdgeFunction(mapperResult.biens, mapperResult.locataires, false);
      if (resp.error) {
        setError(resp.error);
      } else {
        setImportResult(resp);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setMapperResult(null);
    setDryRunStats(null);
    setImportResult(null);
    setError(null);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title={t("importXlsx.title")}
        subtitle={t("importXlsx.subtitle")}
        icon={Upload}
      />

      {isDevSession && (
        <div className="mb-6 p-4 rounded-md bg-warning-light border border-warning text-warning text-sm flex gap-2 items-start">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{t("importXlsx.devSessionWarning")}</span>
        </div>
      )}

      {/* Étape 1 : Upload + Analyse */}
      <SectionCard title={t("importXlsx.step1Title")} icon={FileSpreadsheet} columns={1}>
        <div className="flex items-center gap-4">
          <label className="flex-1">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={parsing || importing}
              className="block w-full text-sm text-text-secondary
                file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
                file:text-sm file:font-medium file:bg-accent file:text-white
                hover:file:bg-accent-hover file:cursor-pointer
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </label>
          <Button
            type="button"
            onClick={handleAnalyse}
            disabled={!file || parsing || importing}
            variant="primary"
          >
            {parsing ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t("importXlsx.analysing")}
              </>
            ) : (
              t("importXlsx.analyse")
            )}
          </Button>
        </div>
        {file && (
          <p className="mt-2 text-xs text-text-muted">
            {t("importXlsx.fileSelected", { name: file.name, size: (file.size / 1024).toFixed(0) })}
          </p>
        )}
      </SectionCard>

      {/* Étape 2 : Preview */}
      {(mapperResult || dryRunStats) && !importResult && (
        <div className="mt-6">
          <SectionCard title={t("importXlsx.step2Title")} icon={FileSpreadsheet} columns={1}>
            {mapperResult && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Stat label={t("importXlsx.statTotal")} value={mapperResult.stats.totalRows} />
                <Stat label={t("importXlsx.statApart")} value={mapperResult.stats.apartmentRows} />
                <Stat label={t("importXlsx.statUnique")} value={mapperResult.stats.uniqueProperties} />
                <Stat label={t("importXlsx.statLoc")} value={mapperResult.stats.locatairesCreated} />
              </div>
            )}

            {dryRunStats && (
              <>
                <div className="border-t border-border my-4" />
                <h4 className="text-xs font-semibold tracking-widest uppercase text-text-muted mb-3">
                  {t("importXlsx.dbImpact")}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <Stat
                    label={t("importXlsx.biensCreated")}
                    value={dryRunStats.biensCreated}
                    tone="success"
                  />
                  <Stat
                    label={t("importXlsx.biensUpdated")}
                    value={dryRunStats.biensUpdated}
                    tone="info"
                  />
                  <Stat
                    label={t("importXlsx.biensSkipped")}
                    value={dryRunStats.biensSkipped}
                    tone="muted"
                  />
                  <Stat
                    label={t("importXlsx.locCreated")}
                    value={dryRunStats.locatairesCreated}
                    tone="success"
                  />
                  <Stat
                    label={t("importXlsx.locUpdated")}
                    value={dryRunStats.locatairesUpdated}
                    tone="info"
                  />
                </div>

                {dryRunStats.errors.length > 0 && (
                  <div className="mt-4 p-3 rounded-md bg-danger-light border border-danger text-danger text-xs">
                    <div className="font-semibold mb-2">
                      {t("importXlsx.errorsFound", { count: dryRunStats.errors.length })}
                    </div>
                    <ul className="space-y-1">
                      {dryRunStats.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>
                          [{e.table}] {e.id} : {e.message}
                        </li>
                      ))}
                      {dryRunStats.errors.length > 10 && (
                        <li className="opacity-70">
                          {t("importXlsx.errorsMore", { count: dryRunStats.errors.length - 10 })}
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="mt-6 p-3 rounded-md bg-accent/5 border border-accent/30 text-xs text-text-secondary">
                  <strong className="text-text-primary">{t("importXlsx.mergeRule")}</strong>{" "}
                  {t("importXlsx.mergeRuleDetail")}
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <Button type="button" onClick={handleReset} variant="cancel">
                    {t("importXlsx.cancel")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    disabled={importing}
                    variant="primary"
                  >
                    {importing ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {t("importXlsx.importing")}
                      </>
                    ) : (
                      t("importXlsx.confirm")
                    )}
                  </Button>
                </div>
              </>
            )}
          </SectionCard>
        </div>
      )}

      {/* Étape 3 : Résultat */}
      {importResult?.stats && (
        <div className="mt-6">
          <SectionCard title={t("importXlsx.resultTitle")} icon={CheckCircle2} columns={1}>
            <div className="mb-4 p-3 rounded-md bg-success-light border border-success text-success text-sm flex gap-2 items-start">
              <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
              <span>{t("importXlsx.success")}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat
                label={t("importXlsx.biensCreated")}
                value={importResult.stats.biensCreated}
                tone="success"
              />
              <Stat
                label={t("importXlsx.biensUpdated")}
                value={importResult.stats.biensUpdated}
                tone="info"
              />
              <Stat
                label={t("importXlsx.biensSkipped")}
                value={importResult.stats.biensSkipped}
                tone="muted"
              />
              <Stat
                label={t("importXlsx.locCreated")}
                value={importResult.stats.locatairesCreated}
                tone="success"
              />
              <Stat
                label={t("importXlsx.locUpdated")}
                value={importResult.stats.locatairesUpdated}
                tone="info"
              />
            </div>
            {importResult.stats.errors.length > 0 && (
              <div className="mt-4 p-3 rounded-md bg-danger-light border border-danger text-danger text-xs">
                <div className="font-semibold mb-2">
                  {t("importXlsx.errorsFound", { count: importResult.stats.errors.length })}
                </div>
                <ul className="space-y-1">
                  {importResult.stats.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>
                      [{e.table}] {e.id} : {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <Button type="button" onClick={handleReset} variant="primary">
                {t("importXlsx.newImport")}
              </Button>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Erreur globale */}
      {error && (
        <div className="mt-6 p-4 rounded-md bg-danger-light border border-danger text-danger text-sm flex gap-2 items-start">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "info" | "muted";
}) {
  const colorMap: Record<typeof tone, string> = {
    default: "text-text-primary",
    success: "text-success",
    info: "text-accent",
    muted: "text-text-muted",
  };
  return (
    <div className="bg-surface border border-border rounded-md p-3">
      <div className="text-[10px] font-semibold tracking-widest uppercase text-text-muted">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${colorMap[tone]}`}>{value}</div>
    </div>
  );
}
